import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../api/mockData';
import './index.css';

const Auth = ({ onLogin, isAuthenticated }) => {
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [catAnimation, setCatAnimation] = useState('idle');
  const [catMessage, setCatMessage] = useState('喵~ 欢迎回来！');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const navigate = useNavigate();

  // 如果已登录，跳转到首页
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // 根据密码强度更新猫咪动画
  useEffect(() => {
    if (formData.password) {
      const strength = Math.min(Math.floor(formData.password.length / 2), 5);
      setPasswordStrength(strength);
      
      if (formData.password.length < 6) {
        setCatAnimation('sad');
        setCatMessage('喵~ 密码有点弱哦！');
      } else if (formData.password.length < 10) {
        setCatAnimation('happy');
        setCatMessage('喵~ 这个密码不错！');
      } else {
        setCatAnimation('excited');
        setCatMessage('喵！超强密码！');
      }
    } else {
      setPasswordStrength(0);
      setCatAnimation('idle');
      setCatMessage(isLoginMode ? '喵~ 欢迎回来！' : '喵~ 加入我们吧！');
    }
  }, [formData.password, isLoginMode]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // 清除对应字段的错误
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // 用户名验证
    if (!formData.username.trim()) {
      newErrors.username = '请输入用户名';
    } else if (formData.username.length < 3) {
      newErrors.username = '用户名至少3个字符';
    } else if (formData.username.length > 20) {
      newErrors.username = '用户名不能超过20个字符';
    }

    // 邮箱验证（仅注册模式）
    if (!isLoginMode) {
      if (!formData.email.trim()) {
        newErrors.email = '请输入邮箱地址';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = '请输入有效的邮箱地址';
      }
    }

    // 密码验证
    if (!formData.password) {
      newErrors.password = '请输入密码';
    } else if (formData.password.length < 6) {
      newErrors.password = '密码至少6个字符';
    }

    // 确认密码验证（仅注册模式）
    if (!isLoginMode) {
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = '请确认密码';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = '两次输入的密码不一致';
      }
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);
    setCatAnimation('loading');

    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      setLoading(false);
      setCatAnimation('error');
      setCatMessage('喵~ 请检查输入！');
      return;
    }

    try {
      if (isLoginMode) {
        // 登录
        const response = await api.login({
          username: formData.username,
          password: formData.password
        });
        
        if (response.success) {
          setCatAnimation('success');
          setCatMessage('登录成功！喵~');
          
          // 调用父组件的登录处理函数
          if (onLogin) {
            onLogin(response.data.user);
          }
          
          // 延迟跳转
          setTimeout(() => {
            navigate('/');
          }, 1500);
        } else {
          setErrors({ general: response.message || '用户名或密码错误' });
          setCatAnimation('error');
          setCatMessage('喵~ 登录失败！');
        }
      } else {
        // 注册
        const response = await api.register({
          username: formData.username,
          email: formData.email,
          name: formData.username,
          password: formData.password
        });
        
        if (response.success) {
          setCatAnimation('success');
          setCatMessage('注册成功！欢迎加入喵星人！');
          
          // 自动切换到登录模式
          setTimeout(() => {
            setIsLoginMode(true);
            setCatAnimation('idle');
            setCatMessage('喵~ 现在可以登录啦！');
            setFormData({
              ...formData,
              email: '',
              confirmPassword: ''
            });
          }, 2000);
        } else {
          setErrors({ general: response.message || '注册失败，请重试' });
          setCatAnimation('error');
          setCatMessage('喵~ 注册失败！');
        }
      }
    } catch (error) {
      setErrors({ general: '网络错误，请稍后重试' });
      setCatAnimation('error');
      setCatMessage('喵~ 网络出错啦！');
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLoginMode(!isLoginMode);
    setErrors({});
    setCatAnimation('idle');
    setCatMessage(!isLoginMode ? '喵~ 欢迎回来！' : '喵~ 加入我们吧！');
  };

  const handleForgotPassword = () => {
    setCatAnimation('help');
    setCatMessage('喵~ 请检查邮箱重置密码！');
    // 这里可以添加忘记密码的逻辑
  };

  const handleQuickDemo = () => {
    setFormData({
      username: 'catlover',
      email: 'catlover@meow.com',
      password: 'meow123',
      confirmPassword: 'meow123',
      rememberMe: true
    });
    setCatAnimation('happy');
    setCatMessage('喵~ 演示账号已填充！');
  };

  // 获取密码强度颜色
  const getPasswordStrengthColor = () => {
    if (passwordStrength <= 2) return '#ff6b6b';
    if (passwordStrength <= 4) return '#ffa726';
    return '#4caf50';
  };

  // 获取密码强度文本
  const getPasswordStrengthText = () => {
    if (passwordStrength <= 2) return '弱';
    if (passwordStrength <= 4) return '中';
    return '强';
  };

  return (
    <div className="auth-page">
      {/* 背景装饰元素 */}
      <div className="auth-background">
        <div className="floating-cats">
          {[...Array(8)].map((_, i) => (
            <div 
              key={i} 
              className={`floating-cat cat-${i + 1}`}
              style={{ animationDelay: `${i * 0.5}s` }}
            >
              {['😺', '😸', '😹', '😻', '😼', '😽', '🙀', '😿'][i]}
            </div>
          ))}
        </div>
        <div className="paw-prints">
          {[...Array(12)].map((_, i) => (
            <div 
              key={i} 
              className="paw-print"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${i * 0.3}s`
              }}
            >
              🐾
            </div>
          ))}
        </div>
      </div>

      {/* 主容器 */}
      <div className="auth-container">
        {/* 左侧装饰区域 */}
        <div className="auth-decoration">
          <div className="decoration-content">
            {/* 猫咪角色 */}
            <div className={`cat-character ${catAnimation}`}>
              <div className="cat-body">
                <div className="cat-ears">
                  <div className="ear left-ear"></div>
                  <div className="ear right-ear"></div>
                </div>
                <div className="cat-face">
                  <div className="cat-eyes">
                    <div className={`eye left-eye ${catAnimation}`}></div>
                    <div className={`eye right-eye ${catAnimation}`}></div>
                  </div>
                  <div className="cat-nose"></div>
                  <div className="cat-mouth"></div>
                  <div className="cat-whiskers">
                    <div className="whisker left-whisker-1"></div>
                    <div className="whisker left-whisker-2"></div>
                    <div className="whisker right-whisker-1"></div>
                    <div className="whisker right-whisker-2"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* 欢迎信息 */}
            <div className="welcome-section">
              <h1 className="welcome-title">
                {isLoginMode ? '欢迎回到' : '加入'}
                <span className="highlight">喵星人日记</span>
              </h1>
              <p className="welcome-subtitle">
                {isLoginMode 
                  ? '继续记录与猫咪共度的美好时光吧！' 
                  : '创建账户，开始分享你的猫咪故事！'
                }
              </p>
              <div className="cat-message">
                <span className="message-icon">💬</span>
                <span className="message-text">{catMessage}</span>
              </div>
            </div>

            {/* 社区统计 */}
            <div className="community-stats">
              <h3 className="stats-title">猫咪社区</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-icon">🐱</div>
                  <div className="stat-info">
                    <div className="stat-value">1,200+</div>
                    <div className="stat-label">猫友</div>
                  </div>
                </div>
                <div className="stat-item">
                  <div className="stat-icon">📝</div>
                  <div className="stat-info">
                    <div className="stat-value">350+</div>
                    <div className="stat-label">文章</div>
                  </div>
                </div>
                <div className="stat-item">
                  <div className="stat-icon">💬</div>
                  <div className="stat-info">
                    <div className="stat-value">2,400+</div>
                    <div className="stat-label">评论</div>
                  </div>
                </div>
              </div>
            </div>

            {/* 猫咪小贴士 */}
            <div className="cat-tips">
              <h3 className="tips-title">🐾 养猫小贴士</h3>
              <ul className="tips-list">
                <li>每天给猫咪梳毛，减少毛球</li>
                <li>提供新鲜的水源，远离猫粮</li>
                <li>定期修剪指甲，保护家具</li>
                <li>多和猫咪互动，增进感情</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 右侧表单区域 */}
        <div className="auth-form-section">
          {/* 模式切换 */}
          <div className="mode-toggle">
            <div className="toggle-buttons">
              <button
                className={`toggle-button ${isLoginMode ? 'active' : ''}`}
                onClick={() => setIsLoginMode(true)}
                disabled={loading}
              >
                <span className="toggle-icon">🔑</span>
                <span className="toggle-text">登录</span>
              </button>
              <button
                className={`toggle-button ${!isLoginMode ? 'active' : ''}`}
                onClick={() => setIsLoginMode(false)}
                disabled={loading}
              >
                <span className="toggle-icon">🐱</span>
                <span className="toggle-text">注册</span>
              </button>
            </div>
            <div className="toggle-indicator">
              <div className={`indicator ${isLoginMode ? 'login' : 'register'}`}></div>
            </div>
          </div>

          {/* 表单标题 */}
          <div className="form-header">
            <h2 className="form-title">
              {isLoginMode ? '登录你的账户' : '创建新账户'}
            </h2>
            <p className="form-subtitle">
              {isLoginMode 
                ? '输入你的凭证继续访问' 
                : '加入喵星人日记社区'
              }
            </p>
          </div>

          {/* 错误消息 */}
          {errors.general && (
            <div className="error-message general-error">
              <span className="error-icon">❌</span>
              {errors.general}
            </div>
          )}

          {/* 表单 */}
          <form className="auth-form" onSubmit={handleSubmit}>
            {/* 用户名 */}
            <div className="form-group">
              <label className="form-label">
                <span className="label-icon">👤</span>
                用户名
              </label>
              <div className="input-wrapper">
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className={`form-input ${errors.username ? 'error' : ''}`}
                  placeholder="输入用户名"
                  disabled={loading}
                  autoComplete="username"
                />
                <span className="input-decoration">🐱</span>
              </div>
              {errors.username && (
                <span className="error-text">{errors.username}</span>
              )}
            </div>

            {/* 邮箱（仅注册模式） */}
            {!isLoginMode && (
              <div className="form-group">
                <label className="form-label">
                  <span className="label-icon">📧</span>
                  邮箱地址
                </label>
                <div className="input-wrapper">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`form-input ${errors.email ? 'error' : ''}`}
                    placeholder="输入邮箱地址"
                    disabled={loading}
                    autoComplete="email"
                  />
                  <span className="input-decoration">✉️</span>
                </div>
                {errors.email && (
                  <span className="error-text">{errors.email}</span>
                )}
              </div>
            )}

            {/* 密码 */}
            <div className="form-group">
              <label className="form-label">
                <span className="label-icon">🔒</span>
                密码
              </label>
              <div className="input-wrapper password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`form-input ${errors.password ? 'error' : ''}`}
                  placeholder="输入密码"
                  disabled={loading}
                  autoComplete={isLoginMode ? 'current-password' : 'new-password'}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  <span className="toggle-icon">
                    {showPassword ? '🙈' : '👁️'}
                  </span>
                </button>
              </div>
              {errors.password && (
                <span className="error-text">{errors.password}</span>
              )}
              
              {/* 密码强度指示器 */}
              {formData.password && !isLoginMode && (
                <div className="password-strength">
                  <div className="strength-label">密码强度：</div>
                  <div className="strength-bars">
                    {[...Array(5)].map((_, i) => (
                      <div
                        key={i}
                        className={`strength-bar ${i < passwordStrength ? 'filled' : ''}`}
                        style={{
                          backgroundColor: i < passwordStrength ? getPasswordStrengthColor() : '#e0e0e0'
                        }}
                      ></div>
                    ))}
                  </div>
                  <div 
                    className="strength-text"
                    style={{ color: getPasswordStrengthColor() }}
                  >
                    {getPasswordStrengthText()}
                  </div>
                </div>
              )}
            </div>

            {/* 确认密码（仅注册模式） */}
            {!isLoginMode && (
              <div className="form-group">
                <label className="form-label">
                  <span className="label-icon">🔒</span>
                  确认密码
                </label>
                <div className="input-wrapper password-input">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                    placeholder="再次输入密码"
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={loading}
                  >
                    <span className="toggle-icon">
                      {showConfirmPassword ? '🙈' : '👁️'}
                    </span>
                  </button>
                </div>
                {errors.confirmPassword && (
                  <span className="error-text">{errors.confirmPassword}</span>
                )}
                
                {/* 密码匹配指示器 */}
                {formData.password && formData.confirmPassword && (
                  <div className={`password-match ${
                    formData.password === formData.confirmPassword ? 'match' : 'mismatch'
                  }`}>
                    {formData.password === formData.confirmPassword ? (
                      <>
                        <span className="match-icon">✅</span>
                        <span className="match-text">密码匹配</span>
                      </>
                    ) : (
                      <>
                        <span className="match-icon">❌</span>
                        <span className="match-text">密码不匹配</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 记住我 & 忘记密码 */}
            <div className="form-options">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleInputChange}
                  className="checkbox-input"
                  disabled={loading}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-text">记住我</span>
              </label>
              
              {isLoginMode && (
                <button
                  type="button"
                  className="forgot-password"
                  onClick={handleForgotPassword}
                  disabled={loading}
                >
                  忘记密码？
                </button>
              )}
            </div>

            {/* 提交按钮 */}
            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="loading-spinner"></span>
                  <span className="button-text">
                    {isLoginMode ? '登录中...' : '注册中...'}
                  </span>
                </>
              ) : (
                <>
                  <span className="button-icon">
                    {isLoginMode ? '🔑' : '🎉'}
                  </span>
                  <span className="button-text">
                    {isLoginMode ? '立即登录' : '创建账户'}
                  </span>
                </>
              )}
            </button>

            {/* 演示按钮 */}
            <button
              type="button"
              className="demo-button"
              onClick={handleQuickDemo}
              disabled={loading}
            >
              <span className="demo-icon">😺</span>
              <span className="demo-text">快速体验（演示账号）</span>
            </button>

            {/* 分隔线 */}
            <div className="divider">
              <span className="divider-text">或者</span>
            </div>

            {/* 第三方登录 */}
            <div className="social-login">
              <div className="social-buttons">
                <button
                  type="button"
                  className="social-button github"
                  disabled={loading}
                >
                  <span className="social-icon">🐙</span>
                  <span className="social-text">GitHub</span>
                </button>
                <button
                  type="button"
                  className="social-button google"
                  disabled={loading}
                >
                  <span className="social-icon">🔍</span>
                  <span className="social-text">Google</span>
                </button>
                <button
                  type="button"
                  className="social-button wechat"
                  disabled={loading}
                >
                  <span className="social-icon">💬</span>
                  <span className="social-text">微信</span>
                </button>
              </div>
            </div>

            {/* 模式切换链接 */}
            <div className="mode-switch">
              <span className="switch-text">
                {isLoginMode ? '还没有账户？' : '已有账户？'}
              </span>
              <button
                type="button"
                className="switch-button"
                onClick={toggleMode}
                disabled={loading}
              >
                {isLoginMode ? '立即注册' : '立即登录'}
              </button>
            </div>
          </form>

          {/* 隐私声明 */}
          <div className="privacy-notice">
            <p className="privacy-text">
              注册即表示您同意我们的
              <a href="#" className="privacy-link">服务条款</a>
              和
              <a href="#" className="privacy-link">隐私政策</a>
              。猫咪会保护您的信息安全 🐾
            </p>
          </div>

          {/* 返回首页 */}
          <div className="back-home">
            <Link to="/" className="home-link">
              <span className="home-icon">←</span>
              <span className="home-text">返回首页</span>
            </Link>
          </div>
        </div>
      </div>

      {/* 底部装饰 */}
      <div className="auth-footer">
        <div className="footer-content">
          <div className="footer-cats">
            <span className="footer-cat">🐱</span>
            <span className="footer-cat">😸</span>
            <span className="footer-cat">😻</span>
            <span className="footer-cat">🐈</span>
          </div>
          <p className="footer-text">
            喵星人日记 © 2023 · 记录每一刻猫咪时光
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;